import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  useRawMaterials,
  useFeedProductionTemplates,
  useFeedProductions,
  useFeedProfitBaseline,
} from '../hooks/useFeed';
import { formatCurrency } from '../utils/format';
import { format, subDays } from 'date-fns';

const KG_PER_BAG = 50;

function calcCostPerBag(materials: any[], overrideRmId?: number, overridePrice?: number): number {
  return materials.reduce((sum: number, m: any) => {
    const qtyPerTonne = parseFloat(m.quantity_per_tonne ?? 0);
    const kgPerBag = (qtyPerTonne / 1000) * KG_PER_BAG;
    const price =
      overrideRmId != null && m.raw_material_id === overrideRmId
        ? (overridePrice ?? 0)
        : parseFloat(m.raw_material?.average_cost_per_unit ?? 0);
    return sum + kgPerBag * price;
  }, 0);
}

const DeltaChip: React.FC<{ delta: number; pct: number }> = ({ delta, pct }) => {
  const up = delta > 0;
  const color = up ? '#ef5350' : delta < 0 ? '#4caf50' : '#888';
  const sign = up ? '+' : '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {up ? (
        <TrendingUpIcon sx={{ fontSize: 16, color }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 16, color }} />
      )}
      <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
        {sign}
        {formatCurrency(delta)} ({sign}
        {pct.toFixed(1)}%)
      </Typography>
    </Box>
  );
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
}> = ({ label, value, sub, accent = '#ff9800' }) => (
  <Paper
    sx={{
      p: 2,
      background: 'linear-gradient(135deg, #424242 0%, #303030 100%)',
      border: '1px solid #555',
      height: '100%',
    }}
  >
    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: accent, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const BreakdownRow: React.FC<{
  material: any;
  isChanged: boolean;
  newPrice?: number;
  totalKgUsed: number;
}> = ({ material, isChanged, newPrice, totalKgUsed }) => {
  const qtyPerTonne = parseFloat(material.quantity_per_tonne ?? 0);
  const kgUsedPerFeedBag = (qtyPerTonne / 1000) * KG_PER_BAG;
  const rmKgPerBag = parseFloat(material.raw_material?.default_kg_per_bag ?? 50);
  const currentPricePerKg = parseFloat(material.raw_material?.average_cost_per_unit ?? 0);
  const currentPricePerRmBag = currentPricePerKg * rmKgPerBag;
  const effectivePricePerKg = isChanged && newPrice != null ? newPrice : currentPricePerKg;
  const effectivePricePerRmBag = effectivePricePerKg * rmKgPerBag;
  const currentCost = kgUsedPerFeedBag * currentPricePerKg;
  const newCost = kgUsedPerFeedBag * effectivePricePerKg;
  const delta = newCost - currentCost;
  const pct = totalKgUsed > 0 ? (kgUsedPerFeedBag / totalKgUsed) * 100 : 0;

  return (
    <TableRow
      hover
      sx={
        isChanged
          ? {
              backgroundColor: 'rgba(255,152,0,0.06)',
              '&:hover': { backgroundColor: 'rgba(255,152,0,0.1)' },
            }
          : {}
      }
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {material.raw_material?.name ?? `#${material.raw_material_id}`}
          {isChanged && (
            <Chip
              label="changed"
              size="small"
              sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#ff9800', color: '#000' }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="right">{kgUsedPerFeedBag.toFixed(2)} kg</TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
          <Box
            sx={{
              width: 50,
              height: 4,
              borderRadius: 2,
              bgcolor: 'action.hover',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${pct}%`,
                height: '100%',
                bgcolor: isChanged ? '#ff9800' : '#42a5f5',
                borderRadius: 2,
              }}
            />
          </Box>
          <Typography variant="caption">{pct.toFixed(1)}%</Typography>
        </Box>
      </TableCell>
      <TableCell align="right">
        {formatCurrency(currentPricePerRmBag)}/bag
        <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>
          {formatCurrency(currentPricePerKg)}/kg
        </Typography>
      </TableCell>
      {isChanged ? (
        <TableCell align="right" sx={{ color: '#ff9800', fontWeight: 600 }}>
          {formatCurrency(effectivePricePerRmBag)}/bag
          <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>
            {formatCurrency(effectivePricePerKg)}/kg
          </Typography>
        </TableCell>
      ) : (
        <TableCell align="right">—</TableCell>
      )}
      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {formatCurrency(newCost)}
        {isChanged && delta !== 0 && (
          <Typography
            variant="caption"
            display="block"
            sx={{ color: delta > 0 ? '#ef5350' : '#4caf50' }}
          >
            {delta > 0 ? '+' : ''}
            {formatCurrency(delta)}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const TemplateBreakdown: React.FC<{
  template: any;
  selectedRmId: number | null;
  newPrice: number | null;
}> = ({ template, selectedRmId, newPrice }) => {
  const [open, setOpen] = useState(false);
  const totalKgUsed =
    template.materials?.reduce(
      (s: number, m: any) => s + (parseFloat(m.quantity_per_tonne ?? 0) / 1000) * KG_PER_BAG,
      0
    ) ?? 0;

  const currentCost = calcCostPerBag(template.materials ?? []);
  const newCost =
    selectedRmId != null && newPrice != null
      ? calcCostPerBag(template.materials ?? [], selectedRmId, newPrice)
      : currentCost;
  const delta = newCost - currentCost;
  const pct = currentCost > 0 ? (delta / currentCost) * 100 : 0;
  const usesThisMaterial =
    selectedRmId != null &&
    template.materials?.some((m: any) => m.raw_material_id === selectedRmId);

  if (!usesThisMaterial && selectedRmId != null) return null;

  return (
    <Paper
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: delta !== 0 ? 'rgba(255,152,0,0.3)' : 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {template.feed_brand?.name} · {template.feed_type?.name}
            {template.name && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 0.75, color: 'text.secondary' }}
              >
                ({template.name})
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            Current / bag
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(currentCost)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            New / bag
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: delta > 0 ? '#ef5350' : delta < 0 ? '#4caf50' : 'text.primary',
            }}
          >
            {formatCurrency(newCost)}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 140 }}>
          {delta !== 0 ? (
            <DeltaChip delta={delta} pct={pct} />
          ) : (
            <Typography variant="caption" sx={{ color: '#888' }}>
              no change
            </Typography>
          )}
        </Box>
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={open}>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell align="right">kg used/bag</TableCell>
                <TableCell align="right">Mix %</TableCell>
                <TableCell align="right">Current price/bag</TableCell>
                <TableCell align="right">New price/bag</TableCell>
                <TableCell align="right">Cost/feed bag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {template.materials?.map((m: any) => (
                <BreakdownRow
                  key={m.raw_material_id}
                  material={m}
                  isChanged={selectedRmId != null && m.raw_material_id === selectedRmId}
                  newPrice={newPrice ?? undefined}
                  totalKgUsed={totalKgUsed}
                />
              ))}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 700,
                    color: delta > 0 ? '#ef5350' : delta < 0 ? '#4caf50' : 'inherit',
                  }}
                >
                  {delta !== 0 && `${delta > 0 ? '+' : ''}${formatCurrency(delta)}`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCurrency(newCost)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

const RawMaterialPriceImpact: React.FC = () => {
  const [selectedRm, setSelectedRm] = useState<any | null>(null);
  const [mode, setMode] = useState<'absolute' | 'percent'>('absolute');
  const [inputValue, setInputValue] = useState('');

  const { data: rawMaterials, isLoading: rmLoading } = useRawMaterials();
  const { data: templates, isLoading: templatesLoading } = useFeedProductionTemplates();
  const ninetyDaysAgo = format(subDays(new Date(), 90), 'yyyy-MM-dd');
  const { data: recentProductions } = useFeedProductions({ date_from: ninetyDaysAgo });
  const { data: baseline } = useFeedProfitBaseline(90);

  const currentPricePerKg = selectedRm ? parseFloat(selectedRm.average_cost_per_unit ?? 0) : 0;
  const rmKgPerBag = selectedRm ? parseFloat(selectedRm.default_kg_per_bag ?? 50) : 50;
  const currentPricePerBag = currentPricePerKg * rmKgPerBag;

  // newPrice is always stored as price-per-kg (what calcCostPerBag expects)
  const newPrice = useMemo<number | null>(() => {
    if (!selectedRm || inputValue === '') return null;
    const val = parseFloat(inputValue);
    if (isNaN(val)) return null;
    if (mode === 'absolute') return val / rmKgPerBag;
    return currentPricePerKg * (1 + val / 100);
  }, [selectedRm, inputValue, mode, currentPricePerKg, rmKgPerBag]);

  const newPricePerBag = newPrice != null ? newPrice * rmKgPerBag : null;
  const priceDeltaPerBag = newPricePerBag != null ? newPricePerBag - currentPricePerBag : 0;

  const affectedTemplates = useMemo(() => {
    if (!templates || !selectedRm) return templates ?? [];
    return (templates as any[]).filter((t) =>
      t.materials?.some((m: any) => m.raw_material_id === selectedRm.id)
    );
  }, [templates, selectedRm]);

  const summaryStats = useMemo(() => {
    if (!affectedTemplates.length || newPrice == null) return null;
    let maxDeltaPerBag = 0;
    let maxTemplate = '';
    let totalDeltaWeighted = 0;
    let totalBagsInPeriod = 0;

    for (const t of affectedTemplates) {
      const current = calcCostPerBag(t.materials ?? []);
      const updated = calcCostPerBag(t.materials ?? [], selectedRm?.id, newPrice);
      const delta = updated - current;
      if (Math.abs(delta) > Math.abs(maxDeltaPerBag)) {
        maxDeltaPerBag = delta;
        maxTemplate = `${t.feed_brand?.name} ${t.feed_type?.name}`;
      }

      // Volume-weighted monthly impact from recent productions
      const matchingProds = (recentProductions ?? []).filter(
        (p: any) => p.feed_type_id === t.feed_type_id && p.feed_brand_id === t.feed_brand_id
      );
      const totalBags90d = matchingProds.reduce(
        (s: number, p: any) => s + parseFloat(p.quantity_produced_bags ?? 0),
        0
      );
      const monthlyBags = (totalBags90d / 90) * 30;
      totalDeltaWeighted += monthlyBags * delta;
      totalBagsInPeriod += totalBags90d;
    }

    return {
      maxDeltaPerBag,
      maxTemplate,
      estimatedMonthlyImpact: totalDeltaWeighted,
      bagsProducedLast90d: totalBagsInPeriod,
    };
  }, [affectedTemplates, newPrice, selectedRm, recentProductions]);

  const loading = rmLoading || templatesLoading;

  return (
    <Box sx={{ p: 3 }}>
      {loading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffa726)' },
          }}
        />
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Raw Material Price Impact
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Model how a price change affects feed production cost and profit
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={(rawMaterials as any[]) ?? []}
              getOptionLabel={(o: any) => o.name}
              value={selectedRm}
              onChange={(_, v) => {
                setSelectedRm(v);
                setInputValue('');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Raw Material" size="small" />
              )}
              isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
            />
            {selectedRm && (
              <Typography
                variant="caption"
                sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}
              >
                Current avg: <strong>{formatCurrency(currentPricePerBag)}</strong> / bag (
                {rmKgPerBag} kg) &nbsp;·&nbsp;{formatCurrency(currentPricePerKg)}/kg
                &nbsp;·&nbsp;Stock: {parseFloat(selectedRm.current_stock ?? 0).toFixed(0)}{' '}
                {selectedRm.unit_of_measure}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => {
                if (v) {
                  setMode(v);
                  setInputValue('');
                }
              }}
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton value="absolute">New price / bag</ToggleButton>
              <ToggleButton value="percent">% change</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              label={mode === 'absolute' ? `New price per bag (GH₵)` : 'Price change (%)'}
              type="number"
              size="small"
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={!selectedRm}
              slotProps={{ htmlInput: { step: mode === 'absolute' ? '0.01' : '1' } }}
              helperText={
                newPricePerBag != null
                  ? `${formatCurrency(newPricePerBag)}/bag · ${formatCurrency(newPrice!)}/kg (${priceDeltaPerBag >= 0 ? '+' : ''}${formatCurrency(priceDeltaPerBag)}/bag)`
                  : mode === 'percent'
                    ? 'e.g. 10 for +10%, -5 for -5%'
                    : ' '
              }
            />
          </Grid>

          {summaryStats && (
            <Grid size={{ xs: 12, md: 5 }}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6 }}>
                  <StatCard
                    label="Max Δ / feed bag"
                    value={`${summaryStats.maxDeltaPerBag >= 0 ? '+' : ''}${formatCurrency(summaryStats.maxDeltaPerBag)}`}
                    sub={summaryStats.maxTemplate}
                    accent={summaryStats.maxDeltaPerBag >= 0 ? '#ef5350' : '#4caf50'}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <StatCard
                    label="Est. monthly impact"
                    value={`${summaryStats.estimatedMonthlyImpact >= 0 ? '+' : ''}${formatCurrency(summaryStats.estimatedMonthlyImpact)}`}
                    sub={`Based on ${summaryStats.bagsProducedLast90d.toFixed(0)} bags (last 90 d)`}
                    accent={summaryStats.estimatedMonthlyImpact >= 0 ? '#ef5350' : '#4caf50'}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Template impact list */}
      {selectedRm && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Affected Feed Templates
            </Typography>
            <Chip
              label={`${affectedTemplates.length} template${affectedTemplates.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#ff9800', color: '#000' }}
            />
            <Tooltip title="Click a row to expand the full material cost breakdown">
              <InfoIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            </Tooltip>
          </Box>

          {affectedTemplates.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {selectedRm.name} is not used in any production template.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {/* Header row */}
              <Box sx={{ display: 'flex', px: 1.5, pb: 0.5, gap: 2 }}>
                <Typography variant="caption" sx={{ flex: 1, color: 'text.disabled' }}>
                  Template
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ minWidth: 90, textAlign: 'right', color: 'text.disabled' }}
                >
                  Current/bag
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ minWidth: 90, textAlign: 'right', color: 'text.disabled' }}
                >
                  New/bag
                </Typography>
                <Typography variant="caption" sx={{ minWidth: 140, color: 'text.disabled' }}>
                  Change
                </Typography>
                <Box sx={{ width: 32 }} />
              </Box>

              {(templates as any[])?.map((t: any) => (
                <TemplateBreakdown
                  key={t.id}
                  template={t}
                  selectedRmId={selectedRm.id}
                  newPrice={newPrice}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {/* Profit impact section */}
      {baseline &&
        selectedRm &&
        newPrice != null &&
        (() => {
          const b = baseline as any;
          const bagsPerCrate: number = parseFloat(b.bags_per_crate ?? 0);
          const revenuePerCrate: number = parseFloat(b.revenue_per_crate ?? 0);
          const currentFeedCostPerCrate: number = parseFloat(b.feed_cost_per_crate ?? 0);
          const monthlyEggCrates: number = parseFloat(b.monthly_egg_crates ?? 0);

          // Weighted avg delta per feed bag across affected templates (by recent production volume)
          let weightedDeltaPerBag = 0;
          let totalWeight = 0;
          for (const t of affectedTemplates) {
            const current = calcCostPerBag(t.materials ?? []);
            const updated = calcCostPerBag(t.materials ?? [], selectedRm.id, newPrice);
            const delta = updated - current;
            const matchingBags = (recentProductions ?? [])
              .filter(
                (p: any) => p.feed_type_id === t.feed_type_id && p.feed_brand_id === t.feed_brand_id
              )
              .reduce((s: number, p: any) => s + parseFloat(p.quantity_produced_bags ?? 0), 0);
            weightedDeltaPerBag += delta * matchingBags;
            totalWeight += matchingBags;
          }
          const avgDeltaPerBag =
            totalWeight > 0
              ? weightedDeltaPerBag / totalWeight
              : affectedTemplates.length > 0
                ? (() => {
                    const t = affectedTemplates[0];
                    return (
                      calcCostPerBag(t.materials ?? [], selectedRm.id, newPrice) -
                      calcCostPerBag(t.materials ?? [])
                    );
                  })()
                : 0;

          const newFeedCostPerCrate = currentFeedCostPerCrate + avgDeltaPerBag * bagsPerCrate;
          const currentMarginPerCrate = revenuePerCrate - currentFeedCostPerCrate;
          const newMarginPerCrate = revenuePerCrate - newFeedCostPerCrate;
          const marginDelta = newMarginPerCrate - currentMarginPerCrate;
          const marginDeltaPct =
            currentMarginPerCrate !== 0 ? (marginDelta / Math.abs(currentMarginPerCrate)) * 100 : 0;
          const currentMarginPct =
            revenuePerCrate > 0 ? (currentMarginPerCrate / revenuePerCrate) * 100 : 0;
          const newMarginPct =
            revenuePerCrate > 0 ? (newMarginPerCrate / revenuePerCrate) * 100 : 0;
          const monthlyProfitImpact = monthlyEggCrates * marginDelta;

          return (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Profit Impact
                </Typography>
                <Chip
                  label={`last 90 d · ${Math.round(parseFloat(b.egg_crates ?? 0)).toLocaleString()} crates produced`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem' }}
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard
                    label="Revenue / crate"
                    value={formatCurrency(revenuePerCrate)}
                    accent="#42a5f5"
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard
                    label="Feed cost / crate (current)"
                    value={formatCurrency(currentFeedCostPerCrate)}
                    sub={`${bagsPerCrate.toFixed(3)} bags/crate`}
                    accent="#ff9800"
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard
                    label="Feed cost / crate (new)"
                    value={formatCurrency(newFeedCostPerCrate)}
                    sub={`${avgDeltaPerBag >= 0 ? '+' : ''}${formatCurrency(avgDeltaPerBag)} avg Δ/bag`}
                    accent={avgDeltaPerBag > 0 ? '#ef5350' : '#4caf50'}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard
                    label="Monthly profit impact"
                    value={`${monthlyProfitImpact >= 0 ? '+' : ''}${formatCurrency(monthlyProfitImpact)}`}
                    sub={`${monthlyEggCrates.toFixed(0)} crates/month est.`}
                    accent={monthlyProfitImpact >= 0 ? '#ef5350' : '#4caf50'}
                  />
                </Grid>
              </Grid>

              {/* Margin bar */}
              <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
                >
                  Gross margin per crate (revenue − feed cost)
                </Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        Current
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {formatCurrency(currentMarginPerCrate)} ({currentMarginPct.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'action.hover',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.max(0, Math.min(100, currentMarginPct))}%`,
                          height: '100%',
                          bgcolor: '#4caf50',
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        After change
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: newMarginPct < currentMarginPct ? '#ef5350' : '#4caf50',
                        }}
                      >
                        {formatCurrency(newMarginPerCrate)} ({newMarginPct.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'action.hover',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.max(0, Math.min(100, newMarginPct))}%`,
                          height: '100%',
                          bgcolor: newMarginPct < currentMarginPct ? '#ef5350' : '#4caf50',
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <DeltaChip delta={marginDelta} pct={marginDeltaPct} />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          );
        })()}

      {!selectedRm && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <TrendingUpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>
            Select a raw material above to model price impact
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Cost changes propagate through every template that uses that material
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RawMaterialPriceImpact;
